import { useEffect, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { api } from "../api";
import { SHELL } from "../constants/testIds";

// UI_STANDARDS.md Part A "Idle-timeout warning before expiry" — super_admin sessions idle-expire
// at 15 minutes server-side (session_utils.py SESSION_IDLE_MIN_PRIV); warn 2 minutes before that,
// any authenticated request (a plain GET /me) refreshes the server-side activity clock.
const IDLE_MINUTES = 15;
const WARN_BEFORE_MS = 2 * 60 * 1000;
const ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "scroll"];

export function useIdleWarning() {
  const [warning, setWarning] = useState(false);
  const warnTimer = useRef(null);
  const expireDelay = IDLE_MINUTES * 60 * 1000 - WARN_BEFORE_MS;

  useEffect(() => {
    const arm = () => {
      clearTimeout(warnTimer.current);
      warnTimer.current = setTimeout(() => setWarning(true), expireDelay);
    };
    const onActivity = () => { if (!warning) arm(); };
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity));
    arm();
    return () => {
      clearTimeout(warnTimer.current);
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const staySignedIn = async () => {
    await api.get("/me");
    setWarning(false);
  };

  return { warning, staySignedIn };
}

export function IdleWarningBanner({ onStaySignedIn }) {
  return (
    <div data-testid={SHELL.idleWarning} className="glass-modal fixed bottom-6 right-6 z-50 rounded-[14px] px-5 py-4 flex items-center gap-3 max-w-sm">
      <AlertTriangle size={18} style={{ color: "var(--status-expiring-text)" }} />
      <div className="text-sm">
        <p className="font-medium" style={{ color: "var(--scarlet-text)" }}>You'll be signed out soon</p>
        <p style={{ color: "var(--scarlet-text-secondary)" }}>Due to inactivity, for your account's security.</p>
      </div>
      <button onClick={onStaySignedIn} className="text-sm font-semibold shrink-0 scarlet-focusable" style={{ color: "var(--scarlet-accent)" }}>
        Stay signed in
      </button>
    </div>
  );
}
